<template>
  <div>

    <modal
      :title="`${context.lps.find(lpContext => lpContext.lpId === locationProductId)?.name}: ${$t('actions.bulk_edit_inventory')}`"
      :submit="submit"
      :submitting="submitting"
      ref="uriInventoryModal"
    >

      <v-row>

        <v-col>
          <date-range-field
            :label="$t('titles.date_range')"
            v-model="formState.dateRange"
            :rules="formRules.dateRange"
            :min="today"
          />
        </v-col>

        <v-col>

          <weekday-field
            :label="$t('titles.weekdays')"
            v-model="formState.weekdays"
            multiple
            :rules="formRules.weekdays"
          />

        </v-col>

      </v-row>

      <o-text-field
        :label="$t('titles.inventory')"
        type="number"
        v-model.number="formState.spacesTotal"
        :rules="formRules.spacesTotal"
        clearable
      />

    </modal>

  </div>
</template>

<script setup lang="ts">

import { reactive, ref, useTemplateRef } from 'vue'

// components
import Modal from '../../../Modal.vue'
import DateRangeField from '../../../forms/DateRangeField.vue'
import WeekdayField from '../../../forms/WeekdayField.vue'

// models
import { Inventory, type UpdateInventoryByLocationIdRequest } from '../../../../models/Inventory'

// misc
// @ts-ignore
import { closeDialog } from 'vue3-promise-dialog'
import { integer, maxValue, minValue, required } from '../../../../validation'
import { Temporal } from '@js-temporal/polyfill'
import { type DateRange, Weekday } from '../../../../types/misc'
import type { UriContext } from './types'
import NotificationService from '../../../../services/NotificationService'

interface Props {
  locationId: number
  locationProductId: number
  context: UriContext
}

const props = defineProps<Props>()

const today = Temporal.Now.plainDateISO().toString()

/**
 *
 */

interface FormState {
  dateRange: DateRange | null
  weekdays: Weekday[]
  spacesTotal: number | null
}

const formState = reactive<FormState>({
  dateRange: null,
  weekdays: [Weekday.Sun, Weekday.Mon, Weekday.Tue, Weekday.Wed, Weekday.Thu, Weekday.Fri, Weekday.Sat],
  spacesTotal: null,
})

 const formRules = {
  dateRange: [
    required(),
  ],
  weekdays: [
    required(),
  ],
  spacesTotal: [
    integer(),
    minValue(0),
    maxValue(props.context.locationSpacesTotal),
   ],
 }

/**
 * Submission
 */

const submitting = ref<boolean>(false)
const uriInventoryModal = useTemplateRef<InstanceType<typeof Modal>>('uriInventoryModal')

async function submit() {

  await uriInventoryModal.value!.modalForm!.validate()

  if (!uriInventoryModal.value!.modalForm!.isValid) {
    return
  }

  submitting.value = true

  const request: UpdateInventoryByLocationIdRequest = {
    start: formState.dateRange!.start,
    end: formState.dateRange!.end,
    weekdays: formState.weekdays,
    inventories: [{
      locationProductId: props.locationProductId,
      spacesTotal: formState.spacesTotal!,
    }]
  }

  Inventory.updateByLocationId(props.locationId, request)
    .then(() => {
      NotificationService.success('Inventory updated')
      closeDialog(true) // TODO: pass anything back?
    })
    .catch((e) => {
      console.error(e)
      NotificationService.error('Couldn\'t update inventory!')
    })
    .finally(() => {
      submitting.value = false
    })

}

</script>
